import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Link,
  Alert,
  Stack,
  TextField,
  Paper,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Login as LoginIcon, Sms } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { validatePhoneNumber, validateCountryCode } from '../utils/phoneUtils';
import CountryCodeSelector from '../components/CountryCodeSelector';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, sendOTP } = useAuth();
  
  // Get the redirect path from location state, default to '/'
  const from = location.state?.from?.pathname || '/';

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    phone: '',
    countryCode: '+91',
    otp: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const steps = ['Enter Phone Number', 'Enter OTP'];

  const validatePhone = (phone) => {
    return validatePhoneNumber(phone);
  };

  const validateCountry = (countryCode) => {
    return validateCountryCode(countryCode);
  };

  const handleSendOTP = async () => {
    setError('');
    setSuccess('');
    
    if (!formData.phone.trim()) {
      setError('Please enter phone number');
      return;
    }
    
    if (!validatePhone(formData.phone)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    if (!validateCountry(formData.countryCode)) {
      setError('Please select a valid country code');
      return;
    }

    setLoading(true);
    try {
      const response = await sendOTP(formData.phone, formData.countryCode);
      setOtpSent(true);
      setSuccess(response.message || 'OTP sent successfully!');
      setActiveStep(1);
    } catch (err) {
      setError(err.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    
    if (!formData.otp.trim()) {
      setError('Please enter OTP');
      return;
    }

    setLoading(true);
    try {
      await login(formData.phone, formData.countryCode, formData.otp);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setLoading(true);
    try {
      await sendOTP(formData.phone, formData.countryCode);
      setSuccess('OTP resent successfully!');
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Stack spacing={3}>
            <CountryCodeSelector
              phoneValue={formData.phone}
              countryCodeValue={formData.countryCode}
              onPhoneChange={(value) => setFormData({ ...formData, phone: value })}
              onCountryCodeChange={(value) => setFormData({ ...formData, countryCode: value })}
              error={!!error && (error.includes('phone') || error.includes('country'))}
              helperText=""
            />
            <Button
              className='form-btn'
              onClick={handleSendOTP}
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 50,
                background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                },
              }}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </Button>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Enter OTP"
              name="otp"
              type="text"
              value={formData.otp}
              onChange={handleChange}
              required
              placeholder="4-digit OTP"
              inputProps={{ maxLength: 4 }}
            />
            <Button
              className='form-btn'
              onClick={handleLogin}
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                borderRadius: 50,
                background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                },
              }}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </Button>
            <Button
              onClick={handleResendOTP}
              variant="text"
              size="small"
              disabled={loading}
              sx={{
                textTransform: 'none',
                color: 'primary.main',
              }}
            >
              Resend OTP
            </Button>
          </Stack>
        );
      default:
        return null;
    }
  };
  return (
    <Box
      sx={{
        pt: { xs: 8, md: 10 },
        pb: 8,
        minHeight: '100vh',
        bgcolor: 'linear-gradient(135deg, #F8F6F1 0%, #F5F2EB 50%, #F0EBE0 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${process.env.PUBLIC_URL}/veda-vine-logo.png)`,
          backgroundSize: '200px',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.03,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card 
          sx={{ 
            boxShadow: '0 20px 40px rgba(201, 169, 97, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
            borderRadius: 4,
            overflow: 'visible',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(201, 169, 97, 0.2)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ mb: 2 }}>
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/veda-vine-logo.png`}
                  alt="Veda & Vine"
                  sx={{
                    height: 120,
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                className='font2'
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                  // mb: 1,
                  background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                WELLNESS FROM WITHIN
              </Typography>
            </Box>

            <Stepper 
              activeStep={activeStep} 
              sx={{ 
                mb: 1,
                '& .MuiStepLabel-root .Mui-active .MuiStepLabel-text': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
                '& .MuiStepLabel-root .Mui-completed .MuiStepLabel-text': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
              }}
            >
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            {renderStepContent(activeStep)}

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                New users will be registered automatically
              </Typography>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  opacity: 0.8,
                  fontSize: '0.75rem'
                }}
              >
                You can update your profile after login
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default LoginPage;
